import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  MenuItem,
  Alert,
  Select,
  OutlinedInput,
  Grid,
} from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import "./ConsentCollection.css";

const ConsentCollection = forwardRef(({}, ref) => {
  const [aadhaar, setAadhaar] = useState("");
  const [consent, setConsent] = useState(false);
  const [authType, setAuthType] = useState("Aadhaar OTP");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showAadhar, setShowAadhar] = useState(false);

  const [aadhaarParts, setAadhaarParts] = useState(["", "", ""]);
  const inputRefs = [useRef(), useRef(), useRef()];
  useImperativeHandle(ref, () => ({
    getDetails,
  }));

  const getDetails = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return false;
    }
    return aadhaarParts.join("");
  };

  const handleChange = (index, value) => {
    setError("");
    if (/^\d{0,4}$/.test(value)) {
      const newParts = [...aadhaarParts];
      newParts[index] = value;
      setAadhaarParts(newParts);

      // Auto-focus to next field
      if (value.length === 4 && index < 2) {
        inputRefs[index + 1].current.focus();
      }
    }
  };

  const validate = () => {
    let aadharVal = parseInt(aadhaarParts.join(""));
    if (!/^\d{12}$/.test(aadharVal)) {
      return "Aadhaar number must be 12 digits";
    }
    if (!consent) {
      return "You must agree to the terms";
    }
    return "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setSubmitted(true);
    // Simulate API call here
  };

  const handleCheckBoxChange = (event) => {
    setError("");
    setConsent(event.target.checked);
  };
  return (
    <Box>
      <Typography className="cc_title" gutterBottom>
        Aadhaar Number <span style={{ color: "#f00" }}>*</span>
      </Typography>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        {aadhaarParts.map((part, index) => (
          <TextField
            key={index}
            size="small"
            inputRef={inputRefs[index]}
            value={part}
            onChange={(e) => handleChange(index, e.target.value)}
            inputProps={{ maxLength: 4 }}
            sx={{ width: "80px" }}
            variant="outlined"
            type={showAadhar ? "text" : "password"}
          />
        ))}
        {showAadhar ? (
          <VisibilityIcon
            sx={{ color: "#f5841f", cursor: "pointer" }}
            onClick={() => {
              setShowAadhar((prevState) => !prevState);
            }}
          />
        ) : (
          <VisibilityOffIcon
            sx={{ color: "#f5841f", cursor: "pointer" }}
            onClick={() => {
              setShowAadhar((prevState) => !prevState);
            }}
          />
        )}
      </Box>
      <Box className="cc_info_box">
        <Box>
          <InfoOutlineIcon
            sx={{ height: "1rem", width: "1rem", color: "#6554c0" }}
          />
        </Box>
        <Box>
          <Typography className="cc_info">
            Please ensure that mobile number is linked with Aadhaar as it will
            be required for OTP authentication.
          </Typography>
          <Typography className="cc_info">
            If you do not have a mobile number linked, visit the nearest{" "}
            <a
              href="https://facility.ndhm.gov.in/searchV2"
              style={{ color: "#D66025", textDecoration: "none !important" }}
            >
              ABDM participating facility
            </a>{" "}
            and seek assistance.
          </Typography>
        </Box>
      </Box>
      <Box className="cc_tc_wrapper_box">
        <Box className="cc_tc_box">
          <Typography className="cc_tc_title">Terms and Conditions</Typography>
          <Typography>
            I, hereby declare that I am voluntarily sharing my Aadhaar number
            and demographic information issued by UIDAI, with National Health
            Authority (NHA) for the sole purpose of creation of ABHA number. I
            understand that my ABHA number can be used and shared for purposes
            as may be notified by ABDM from time to time including provision of
            healthcare services. Further, I am aware that my personal
            identifiable information (Name, Address, Age, Date of Birth, Gender
            and Photograph) may be made available to the entities working in the
            National Digital Health Ecosystem (NDHE) which inter alia includes
            stakeholders and entities such as healthcare professionals (e.g.
            doctors), facilities (e.g. hospitals, laboratories) and data
            fiduciaries (e.g. health programmes), which are registered with or
            linked to the Ayushman Bharat Digital Mission (ABDM), and various
            processes there under. I authorize NHA to use my Aadhaar number for
            performing Aadhaar based authentication with UIDAI as per the
            provisions of the Aadhaar (Targeted Delivery of Financial and other
            Subsidies, Benefits and Services) Act, 2016 for the aforesaid
            purpose. I understand that UIDAI will share my e-KYC details, or
            response of “Yes” with NHA upon successful authentication. I have
            been duly informed about the option of using other IDs apart from
            Aadhaar; however, I consciously choose to use Aadhaar number for the
            purpose of availing benefits across the NDHE. I am aware that my
            personal identifiable information excluding Aadhaar number / VID
            number can be used and shared for purposes as mentioned above. I
            reserve the right to revoke the given consent at any point of time
            as per provisions of Aadhaar Act and Regulations.
          </Typography>
        </Box>
        <Box className="cc_tc_ch_box">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={consent}
                onChange={handleCheckBoxChange}
              />
            }
            label="I agree"
          />
        </Box>
      </Box>
      <Box className="cc_at_box">
        <Grid container>
          <Grid item size={{ xs: 12, md: 6 }}>
            <Typography className="cc_tc_title">
              Authentication type <span style={{ color: "#f00" }}>*</span>
            </Typography>
            <Box className="nh_search_select">
              <Select
                displayEmpty
                value={authType}
                input={<OutlinedInput />}
                inputProps={{ "aria-label": "Without label" }}
                className="nh_tpa_btn fs-16-600"
                SelectDisplayProps={{
                  style: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                sx={{
                  marginTop: ".5rem",
                }}
                fullWidth
                onChange={(e) => setAuthType(e.target.value)}
              >
                <MenuItem value={"Aadhaar OTP"}>Aadhaar OTP</MenuItem>
              </Select>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {/* <Box>
        <Button variant="contained" size="small">
          Cancel
        </Button>
        <Button variant="contained" size="small">
          Next
        </Button>
      </Box> */}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {submitted && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Form submitted successfully!
        </Alert>
      )}
    </Box>
  );
});

export default ConsentCollection;
